import { type Festival, festivalPosting } from '@/data/festivals';
import { ITEMS, type ItemId } from '@/data/items';
import { NPC_IDS, type NpcId } from '@/data/npcs';
import type { Friendships } from '@/world/friendship';
import type { Inventory } from '@/world/inventory';
import type { Wallet } from '@/world/wallet';

/**
 * Turning up with something.
 *
 * The festival itself is derived from the date and needs nothing saved. What
 * is saved is which ones you have entered, keyed by year, so a festival comes
 * round again next year and cannot be entered twice in one afternoon.
 *
 * Entering costs the item. It has been eaten, admired, or hung on a line, and
 * the point of bringing the best one is that you brought it rather than kept
 * it.
 */

/** What turning up is worth, before what you brought is judged. */
export const FESTIVAL_PRIZE = 60;
/** And what the host thinks of you for it. */
export const FESTIVAL_FRIENDSHIP = 25;
/** Everyone else there notices too, but less. */
export const FESTIVAL_ONLOOKER_FRIENDSHIP = 10;
/** A entry worth this much or more takes the ribbon rather than a nod. */
export const RIBBON_VALUE = 100;

export interface EntryContext {
  readonly inventory: Inventory;
  readonly wallet: Wallet;
  readonly friendships: Friendships;
  readonly hour: number;
  readonly year: number;
}

export interface EntryOutcome {
  readonly item: ItemId;
  readonly coins: number;
  readonly ribbon: boolean;
}

export class Festivals {
  /** `${year}:${festivalId}` for each one already entered. */
  private readonly entered = new Set<string>();

  private key(festival: Festival, year: number): string {
    return `${year}:${festival.id}`;
  }

  hasEntered(festival: Festival, year: number): boolean {
    return this.entered.has(this.key(festival, year));
  }

  /**
   * The best thing you are carrying that the festival wants.
   *
   * Best by what it is worth, which is the only ordering the game already
   * has an opinion about — and it happens to be the right one: a golden carp
   * should beat a minnow, and a pumpkin a turnip.
   */
  bestEntry(festival: Festival, inventory: Inventory): ItemId | null {
    let best: ItemId | null = null;
    let bestValue = -1;

    for (let i = 0; i < inventory.capacity; i++) {
      const slot = inventory.slotAt(i);
      if (!slot || !festival.wants(slot.id)) continue;

      const value = ITEMS[slot.id].sellPrice ?? 0;
      if (value > bestValue) {
        bestValue = value;
        best = slot.id;
      }
    }

    return best;
  }

  /** Hand it over. Returns null when there is nothing to hand over. */
  enter(festival: Festival, context: EntryContext): EntryOutcome | null {
    if (this.hasEntered(festival, context.year)) return null;

    const item = this.bestEntry(festival, context.inventory);
    if (!item || context.inventory.remove(item, 1) !== 1) return null;

    const value = ITEMS[item].sellPrice ?? 0;
    const coins = FESTIVAL_PRIZE + value * 2;

    context.wallet.earn(coins);
    this.entered.add(this.key(festival, context.year));

    // The host is pleased; everyone else there saw you do it.
    context.friendships.add(festival.host, FESTIVAL_FRIENDSHIP);
    for (const id of attendees(festival, context.hour)) {
      if (id !== festival.host) context.friendships.add(id, FESTIVAL_ONLOOKER_FRIENDSHIP);
    }

    return { item, coins, ribbon: value >= RIBBON_VALUE };
  }

  toSave(): string[] {
    return [...this.entered];
  }

  static fromSave(value: unknown): Festivals {
    const festivals = new Festivals();
    if (!Array.isArray(value)) return festivals;

    for (const key of value) {
      if (typeof key === 'string') festivals.entered.add(key);
    }
    return festivals;
  }
}

/** Who is actually standing about at this hour. */
function attendees(festival: Festival, hour: number): NpcId[] {
  return NPC_IDS.filter((id) => festivalPosting(festival, id, hour) !== null);
}
